import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Payroll, Employee } from './hooks/usePayrollData';
import { formatCurrency } from './utils/payrollUtils';
import { months, statusColors, statusLabels } from './utils/payrollConstants';

interface PayrollDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payroll: Payroll | null;
  employee: Employee | null;
  getPositionName: (posId: number | string) => string;
}

export const PayrollDetailModal: React.FC<PayrollDetailModalProps> = ({
  open,
  onOpenChange,
  payroll,
  employee,
  getPositionName,
}) => {
  if (!payroll || !employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detail Payroll - {employee.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">NIP</p>
              <p className="font-medium">{employee.employee_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Jabatan</p>
              <p className="font-medium">{getPositionName(employee.position_id || '')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Periode</p>
              <p className="font-medium">
                {months.find(m => m.value === payroll.period_month)?.label} {payroll.period_year}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge className={statusColors[payroll.status] || statusColors.draft}>
                {statusLabels[payroll.status] || payroll.status}
              </Badge>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <tbody>
                <tr className="border-b bg-blue-50">
                  <td className="p-3 font-medium">Gaji Pokok</td>
                  <td className="p-3 text-right font-mono">
                    {formatCurrency(payroll.base_salary)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6 text-sm">Tunjangan Tetap</td>
                  <td className="p-3 text-right font-mono text-sm">
                    {formatCurrency(payroll.allowances)}
                  </td>
                </tr>
                <tr className="border-b bg-orange-50">
                  <td className="p-3 font-medium">
                    Lembur
                    {payroll.overtime_hours && payroll.overtime_hours > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({payroll.overtime_hours} jam)
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono text-orange-600 font-semibold">
                    {formatCurrency(payroll.overtime_pay)}
                  </td>
                </tr>
                <tr className="border-b bg-purple-50">
                  <td className="p-3 font-medium">
                    Bonus KPI
                    {payroll.kpi_score && payroll.kpi_score > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        (Score: {payroll.kpi_score})
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono text-purple-600 font-semibold">
                    {formatCurrency(payroll.kpi_bonus)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6 text-sm">Bonus Lainnya</td>
                  <td className="p-3 text-right font-mono text-sm">
                    {formatCurrency(payroll.bonus)}
                  </td>
                </tr>
                <tr className="border-b bg-green-50">
                  <td className="p-3 font-semibold">Gaji Kotor</td>
                  <td className="p-3 text-right font-mono font-bold text-green-600">
                    {formatCurrency(payroll.gross_salary)}
                  </td>
                </tr>
                <tr className="border-b bg-red-50">
                  <td className="p-3 font-medium text-red-700">Potongan</td>
                  <td className="p-3 text-right"></td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6 text-sm">Pajak (PPh)</td>
                  <td className="p-3 text-right font-mono text-sm text-red-600">
                    -{formatCurrency(payroll.tax)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6 text-sm">BPJS Kesehatan</td>
                  <td className="p-3 text-right font-mono text-sm text-red-600">
                    -{formatCurrency(payroll.bpjs_employee)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6 text-sm">Potongan Lain</td>
                  <td className="p-3 text-right font-mono text-sm text-red-600">
                    -{formatCurrency(payroll.deductions)}
                  </td>
                </tr>
                <tr className="bg-emerald-100">
                  <td className="p-4 font-bold text-lg">GAJI BERSIH (Take Home Pay)</td>
                  <td className="p-4 text-right font-mono font-bold text-xl text-emerald-700">
                    {formatCurrency(payroll.net_salary)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
